/**
 * `/settings` — Settings & staff (§3 roles and separation of duties, §16.2): the staff roster of the tenant with
 * invitations and role / active changes through the identity admin routes (`GET/POST /v1/admin/staff`,
 * `PATCH /v1/admin/staff/:id`). The API enforces the §3 role matrix (`separation_of_duties`), that nobody edits
 * their own roles or deactivates themselves, and that only platform super-admins grant that role; this page shows
 * those rules up front and surfaces the API's problem detail inside the dialog when a change is refused. Tenant
 * policy, providers, feature flags, MFA resets and JIT access grants are listed honestly as not wired (D-2204).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { StaffRole, type StaffUserDto } from '@windrop/contracts';
import { Button, Checkbox, Field, Input, Modal, useIntl, useToast } from '@windrop/ui';
import { useMemo, useState } from 'react';
import { useSession } from '../../auth/session';
import { DataTable, type Column } from '../../components/DataTable';
import { EmptyState, ErrorState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { ToneBadge, humanizeStatus } from '../../components/StatusBadge';
import { isApiError } from '../../lib/api/client';
import { staffApi } from '../../lib/api/staff';

/** Who may open this console (identity `admin/staff` routes: `anyRole` ORGANIZER_ADMIN / PLATFORM_SUPER_ADMIN). */
export const STAFF_ADMIN_ROLES: readonly StaffRole[] = ['ORGANIZER_ADMIN', 'PLATFORM_SUPER_ADMIN'];

/** §3 separation of duties as the identity module enforces it (`SOD_INCOMPATIBLE_ROLES`, `EXCLUSIVE_ROLES`). */
export const SOD_INCOMPATIBLE: ReadonlyArray<readonly [StaffRole, StaffRole]> = [
  ['ORGANIZER_ADMIN', 'LOTTERY_APPROVER'],
  ['ORGANIZER_ADMIN', 'DRAW_OPERATOR'],
  ['LOTTERY_APPROVER', 'DRAW_OPERATOR'],
  ['FINANCE', 'DRAW_OPERATOR'],
  ['FINANCE', 'CLAIMS_REVIEWER'],
  ['ENGAGEMENT_ADMIN', 'CLAIMS_REVIEWER'],
  ['ENGAGEMENT_ADMIN', 'FINANCE'],
];
export const EXCLUSIVE_ROLES: readonly StaffRole[] = ['SPONSOR_VIEWER', 'PLATFORM_SUPER_ADMIN'];

/** Why a role set would be refused by the API (undefined when it is acceptable). Mirrors `assertRoleSet`. */
export const roleSetProblem = (roles: readonly StaffRole[]): string | undefined => {
  if (roles.length === 0) return 'Choose at least one role.';
  const set = new Set(roles);
  for (const r of EXCLUSIVE_ROLES)
    if (set.has(r) && set.size > 1) return `${humanizeStatus(r)} cannot be combined with other roles.`;
  for (const [a, b] of SOD_INCOMPATIBLE)
    if (set.has(a) && set.has(b))
      return `${humanizeStatus(a)} and ${humanizeStatus(b)} cannot be held by the same person (§3).`;
  return undefined;
};

/** Sponsor viewers are scoped to one sponsor (D11) and are invited from the sponsor's page, not here. */
const INVITABLE_ROLES = StaffRole.options.filter((r) => r !== 'SPONSOR_VIEWER');

const describeError = (e: unknown, fallback: string): string =>
  isApiError(e)
    ? `${e.problem?.detail ?? e.message}${e.supportReference ? ` (ref ${e.supportReference})` : ''}`
    : fallback;

function RoleChecklist({
  roles,
  onChange,
  options,
  disabled,
}: {
  roles: StaffRole[];
  onChange: (next: StaffRole[]) => void;
  options: readonly StaffRole[];
  disabled?: boolean;
}) {
  return (
    <fieldset className="wd-stack" data-testid="role-checklist">
      <legend>Roles</legend>
      {options.map((r) => (
        <Checkbox
          key={r}
          label={humanizeStatus(r)}
          checked={roles.includes(r)}
          disabled={disabled}
          onCheckedChange={(on) => onChange(on ? [...roles, r] : roles.filter((x) => x !== r))}
        />
      ))}
    </fieldset>
  );
}

export function StaffPage() {
  const { session } = useSession();
  const intl = useIntl();
  const qc = useQueryClient();
  const { toast } = useToast();
  const isSuperAdmin = session?.roles.includes('PLATFORM_SUPER_ADMIN') ?? false;
  const allowed = session?.roles.some((r) => STAFF_ADMIN_ROLES.includes(r)) ?? false;
  const [showInactive, setShowInactive] = useState(false);
  const query = useQuery({
    queryKey: ['staff', { showInactive }],
    queryFn: () => staffApi.list({ limit: 100, ...(showInactive ? {} : { active: true }) }),
    enabled: allowed,
  });

  const [dialog, setDialog] = useState<null | 'invite' | 'edit'>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<StaffUserDto | null>(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [roles, setRoles] = useState<StaffRole[]>([]);
  const [active, setActive] = useState(true);

  const roleOptions = isSuperAdmin
    ? INVITABLE_ROLES
    : INVITABLE_ROLES.filter((r) => r !== 'PLATFORM_SUPER_ADMIN');
  const roleProblem = roleSetProblem(roles);

  const openInvite = () => {
    setError(null);
    setEmail('');
    setName('');
    setRoles([]);
    setDialog('invite');
  };
  const openEdit = (s: StaffUserDto) => {
    setError(null);
    setEditing(s);
    setName(s.name);
    setRoles(s.roles);
    setActive(s.active);
    setDialog('edit');
  };
  const done = (title: string) => {
    void qc.invalidateQueries({ queryKey: ['staff'] });
    toast({ title, tone: 'success' });
    setDialog(null);
  };
  const invite = useMutation({
    mutationFn: () => staffApi.invite({ email: email.trim(), name: name.trim(), roles }),
    onSuccess: (s) => done(`Invitation sent to ${s.email}`),
    onError: (e) => setError(describeError(e, 'The invitation could not be sent. Retry in a moment.')),
  });
  const update = useMutation({
    mutationFn: () => {
      const s = editing!;
      const body = {
        ...(name.trim() !== s.name ? { name: name.trim() } : {}),
        ...(roles.join() !== s.roles.join() ? { roles } : {}),
        ...(active !== s.active ? { active } : {}),
      };
      return staffApi.update(s.staffId, body);
    },
    onSuccess: (s) => done(`${s.name} updated`),
    onError: (e) => setError(describeError(e, 'The change could not be saved. Retry in a moment.')),
  });

  const columns = useMemo<Column<StaffUserDto>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        sortValue: (s) => s.name.toLowerCase(),
        filterValue: (s) => `${s.name} ${s.email}`,
        cell: (s) => (
          <>
            {s.name}
            {s.staffId === session?.staffId ? <span className="wd-muted"> (you)</span> : null}
            <br />
            <span className="wd-muted">{s.email}</span>
          </>
        ),
      },
      {
        id: 'roles',
        header: 'Roles',
        filterValue: (s) => s.roles.join(' '),
        cell: (s) => (
          <span className="wdc-inline-list">
            {s.roles.map((r) => (
              <ToneBadge key={r} tone="neutral">
                {humanizeStatus(r)}
              </ToneBadge>
            ))}
          </span>
        ),
      },
      {
        id: 'active',
        header: 'Status',
        sortValue: (s) => (s.active ? 0 : 1),
        cell: (s) =>
          s.active ? (
            <ToneBadge tone="success">Active</ToneBadge>
          ) : (
            <ToneBadge tone="warning">Deactivated</ToneBadge>
          ),
      },
      {
        id: 'lastLogin',
        header: 'Last sign-in',
        sortValue: (s) => s.lastLoginAt ?? '',
        cell: (s) => (s.lastLoginAt ? intl.formatDateTime(s.lastLoginAt) : 'Never'),
      },
    ],
    [intl, session?.staffId],
  );

  if (!allowed) {
    return (
      <>
        <PageHeader title="Settings & staff" />
        <EmptyState
          title="Organizer admins manage staff"
          body="This console needs the Organizer admin role (or a platform super-admin grant). Ask your organizer admin to change roles or invite colleagues."
          action={<Button href="/">Back to dashboard</Button>}
        />
      </>
    );
  }

  const editingSelf = editing?.staffId === session?.staffId;
  const dialogError = error ? (
    <p role="alert" className="wd-field__error" data-testid="staff-dialog-error">
      {error}
    </p>
  ) : null;

  return (
    <>
      <PageHeader
        title="Settings & staff"
        breadcrumb={[{ label: 'Settings & staff' }]}
        description="Who can sign in to this organizer's console and what each person may do (§3). Invitations send a sign-in link; the first sign-in enrols an authenticator (§17.1)."
        actions={
          <Button variant="primary" onClick={openInvite} data-testid="staff-invite">
            Invite staff
          </Button>
        }
      />
      <section className="wdc-panel" data-testid="staff-roster">
        <div className="wdc-panel__head">
          <h2>Staff</h2>
          <Checkbox
            label="Show deactivated accounts"
            checked={showInactive}
            onCheckedChange={setShowInactive}
          />
        </div>
        {query.isError ? (
          <ErrorState
            title="Staff list did not load"
            body="Retry in a moment."
            reference={isApiError(query.error) ? query.error.supportReference : undefined}
            action={<Button onClick={() => query.refetch()}>Retry</Button>}
          />
        ) : (
          <DataTable<StaffUserDto>
            caption="Staff users"
            columns={columns}
            rows={query.data?.items ?? []}
            rowKey={(s) => s.staffId}
            loading={query.isPending}
            initialSort={{ columnId: 'name', direction: 'asc' }}
            filterPlaceholder="Filter by name, email or role"
            emptyState={
              <EmptyState
                title="No staff yet"
                body="Invite your first colleague."
                action={<Button onClick={openInvite}>Invite staff</Button>}
              />
            }
            rowActions={(s) => (
              <Button variant="secondary" onClick={() => openEdit(s)} data-testid={`staff-edit-${s.staffId}`}>
                Edit
              </Button>
            )}
            testId="staff-table"
          />
        )}
      </section>
      <section className="wdc-panel" data-testid="settings-not-wired">
        <h2>Not wired in this build</h2>
        <p className="wd-muted">
          These §16.2 settings have no console page yet; they are not hidden from you by permission.
        </p>
        <ul>
          <li>
            MFA reset for a colleague who lost their authenticator (identity module; ask the platform
            operator).
          </li>
          <li>Tenant policy such as the high-value threshold (seeded market policy; no admin route yet).</li>
          <li>
            Payment and verification providers and feature flags (set through configuration, never from the
            console).
          </li>
          <li>
            Platform super-admin JIT access grants (`/v1/admin/access-grants`, super-admins only; CLI / API
            for now).
          </li>
        </ul>
      </section>

      <Modal
        open={dialog === 'invite'}
        onOpenChange={(o) => !o && setDialog(null)}
        title="Invite a staff member"
        description="They receive a sign-in link and enrol an authenticator on first sign-in. Roles follow the §3 separation of duties."
        actions={
          <Button
            variant="primary"
            loading={invite.isPending}
            disabled={
              !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim()) || name.trim().length < 1 || !!roleProblem
            }
            disabledReason={roleProblem}
            onClick={() => invite.mutate()}
            data-testid="staff-invite-submit"
          >
            Send invitation
          </Button>
        }
      >
        {dialogError}
        <Field label="Work email" required>
          <Input preset="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Name" required>
          <Input value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
        </Field>
        <RoleChecklist roles={roles} onChange={setRoles} options={roleOptions} />
      </Modal>

      <Modal
        open={dialog === 'edit'}
        onOpenChange={(o) => !o && setDialog(null)}
        title={editing ? `Edit ${editing.name}` : 'Edit staff member'}
        description={
          editingSelf
            ? 'You can change your own name here; roles and deactivation of your own account need another organizer admin (§3).'
            : 'Role changes apply at the next request; deactivation ends open sessions.'
        }
        actions={
          <Button
            variant="primary"
            loading={update.isPending}
            disabled={name.trim().length < 1 || !!roleProblem}
            disabledReason={roleProblem}
            onClick={() => update.mutate()}
            data-testid="staff-edit-submit"
          >
            Save changes
          </Button>
        }
      >
        {dialogError}
        <Field label="Name" required>
          <Input value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
        </Field>
        <RoleChecklist roles={roles} onChange={setRoles} options={roleOptions} disabled={editingSelf} />
        <Checkbox
          label="Account active"
          description={
            editingSelf ? 'You cannot deactivate yourself.' : 'Unticked: the person can no longer sign in.'
          }
          checked={active}
          disabled={editingSelf}
          onCheckedChange={setActive}
        />
      </Modal>
    </>
  );
}
